import { FC } from 'react';

import { useGetDegreesQuery } from '@teachur/http-client';
import type { EvaluationResponse } from '@teachur/http-client';
import { DescriptionList } from '@new/common/ui/DescriptionList/DescriptionList';

import { EvaluationStatusBadge } from '@new/features/transcripts/shared/components/StatusBadge';
import type { EvaluationStatus } from '@new/features/transcripts/shared/types';

interface EvaluationGeneralInfoProps {
  evaluation: EvaluationResponse;
}

const humanize = (value: string | null): string =>
  value ? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

export const EvaluationGeneralInfo: FC<EvaluationGeneralInfoProps> = ({ evaluation }) => {
  const { data: degrees = [] } = useGetDegreesQuery();
  const targetProgramName =
    degrees.find((d) => d.id === evaluation.targetDegreeId)?.name ??
    evaluation.targetProgram ??
    '—';
  const priorDegree = evaluation.degreeName
    ? `${evaluation.degreeName}${
        evaluation.degreeInstitution ? ` · ${evaluation.degreeInstitution}` : ''
      }`
    : '—';

  return (
    <section className="overflow-hidden rounded-lg border border-(--neutral-black-NB200)">
      <DescriptionList>
        <DescriptionList.Row label="Status">
          <EvaluationStatusBadge status={evaluation.status as EvaluationStatus} />
        </DescriptionList.Row>
        <DescriptionList.Row label="Approved credits">
          {evaluation.officialTotal}
        </DescriptionList.Row>
        <DescriptionList.Row label="Requested credits">
          {evaluation.unofficialTotal}
        </DescriptionList.Row>
        <DescriptionList.Row label="Prior education">
          {humanize(evaluation.priorEducationStatus)}
        </DescriptionList.Row>
        <DescriptionList.Row label="Target degree">
          {humanize(evaluation.targetDegreeType)}
        </DescriptionList.Row>
        <DescriptionList.Row label="Target program">{targetProgramName}</DescriptionList.Row>
        <DescriptionList.Row label="Prior degree">{priorDegree}</DescriptionList.Row>

        {evaluation.formerName ? (
          <DescriptionList.Row label="Former name">{evaluation.formerName}</DescriptionList.Row>
        ) : null}
        {evaluation.nameChangeReason ? (
          <DescriptionList.Row label="Name change reason">
            {evaluation.nameChangeReason}
          </DescriptionList.Row>
        ) : null}
        {evaluation.nameChangeDocumentUrl ? (
          <DescriptionList.Row label="Name change document">
            <a
              href={evaluation.nameChangeDocumentUrl}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-(--primary-O900) underline"
            >
              View document
            </a>
          </DescriptionList.Row>
        ) : null}
      </DescriptionList>
    </section>
  );
};
